import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Link } from '@heidi/design-system/link';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ModalId, ModalViewAction, UserRole } from '@heidi/schema/ml-scribe';
import { useCurrentWorkspace } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { getHeidi2Ports, heidi2RequestAccessModalOpenAtom } from '../../../data/heidi-2';

import { isClinicianTrialModalBlockingHeidi2OptIn } from './heidi-2-opt-in-eligibility';
import { HEIDI_2_GETTING_STARTED_URL } from './heidi-2-request-access-banner-constants';
import { computeShouldShowHeidi2RequestAccess } from './heidi-2-request-access-eligibility';

/**
 * When this module loaded. Billing rehydrates from storage as `success` with
 * the previous session's `heidi_version` and only then refetches.
 */
const APP_STARTED_AT = Date.now();

/** Boot routes render this layout, then a redirect tears it down. */
const isBootRoute = (pathname: string) =>
  pathname === '/scribe' || pathname.startsWith('/scribe/new-user');

const messages = defineMessages({
  body: {
    defaultMessage: 'Heidi II is here to handle work around the visit.',
    id: 'UvFb9F7W8b',
    description:
      'Message in the banner telling a non-admin clinician that Heidi II handles work around the visit',
  },
  learnMore: {
    defaultMessage: 'Learn more',
    id: '6YcHh1p+xK',
    description:
      'Link in the Heidi II request-access banner that opens the getting-started article',
  },
  requestAccess: {
    defaultMessage: 'Request access',
    id: '5kj0ufcSvK',
    description:
      'Button in the Heidi II request-access banner that opens the request-access modal',
  },
  dismissLabel: {
    defaultMessage: 'Dismiss Heidi II banner',
    id: 'anfHXExSqa',
    description: 'Accessible label for dismissing the Heidi II request-access banner',
  },
});

/**
 * In-flow T4 chrome for Heidi 1 clinicians who are not workspace admins.
 * Not queued. R1/R2 live in the gate; one dismiss hides it for good.
 */
export const Heidi2RequestAccessBanner = () => {
  const intl = useIntl();
  const ports = getHeidi2Ports();
  const pathname = ports.usePathname();
  const isHeidi2OptInEnabled = ports.useIsHeidi2OptInEnabled();
  const isWorkspaceIdSettled = ports.useWorkspaceIdSettledForCreditUsagePopoverLaunch();
  const {
    data: billingInfo,
    isPending: isBillingInfoPending,
    dataUpdatedAt: billingInfoUpdatedAt,
  } = ports.useUserBillingInfo();
  const hasFreshBillingInfo = billingInfoUpdatedAt >= APP_STARTED_AT;
  const modalAppState = ports.useModalLaunchState();
  const seenModalIds = ports.useSeenModalIds();
  const isClinicianTrialEligibilityLoaded =
    modalAppState.isUserDataLoaded &&
    !modalAppState.isUserPending &&
    !modalAppState.isKindePropertiesPending &&
    seenModalIds !== null &&
    !isBillingInfoPending &&
    hasFreshBillingInfo;
  const markModalAsViewed = ports.useMarkModalAsViewed();
  const setRequestAccessModalOpen = useSetAtom(heidi2RequestAccessModalOpenAtom);
  const [isHiddenLocally, setIsHiddenLocally] = useState(false);
  const isClinicalRecordingActive = ports.useIsClinicalRecordingActive(false);
  const viewTrackedRef = useRef(false);

  const currentWorkspace = useCurrentWorkspace();
  const { data: user } = ports.useQueryUser((record) =>
    record
      ? {
          isManaged: record.is_managed,
          modalView:
            record.seen_modals?.[
              ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_REQUEST_ACCESS_BANNER
            ],
        }
      : undefined
  );
  const isDismissed = ports.isModalClosed(
    ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_REQUEST_ACCESS_BANNER,
    user?.modalView
  );

  const currentQueuedModalId = ports.useCurrentQueuedModalId();
  const queueHeadModalId = ports.useQueueHeadModalId();
  // Claim while the cohort is active even during recording so lower-priority
  // strips stay suppressed and the clinical workspace does not reflow. Paint
  // only when recording is idle.
  const isCohortEligible = computeShouldShowHeidi2RequestAccess(
    {
      isHeidi2OptInEnabled,
      isWorkspaceIdSettled,
      isClinicalRecordingActive: false,
      isUserManaged: user?.isManaged,
      isWorkspaceResolved: currentWorkspace != null,
      isWorkspaceAdmin: currentWorkspace?.user_role === UserRole.ADMIN,
      isClinicianTrialModalBlocking: isClinicianTrialModalBlockingHeidi2OptIn({
        currentQueuedModalId,
        queueHeadModalId,
      }),
      isClinicianTrialEligibilityLoaded,
      isMaxBillingSeat: ports.isMaxBillingSeat,
    },
    billingInfo
  );
  const isActive =
    isCohortEligible && !isDismissed && !isHiddenLocally && !isBootRoute(pathname);
  const holdsSlot = ports.useClaimTopStrip('heidi-2-request-access', isActive);
  const isVisible = holdsSlot && !isClinicalRecordingActive;
  ports.useReportTopStrip(isVisible);

  useEffect(() => {
    if (!isVisible || viewTrackedRef.current) return;

    viewTrackedRef.current = true;
    ports.trackHeidi2RequestAccessBannerViewed();
  }, [isVisible, ports]);

  const handleLearnMore = useCallback(() => {
    ports.trackButtonClicked({
      screen_name: ScreenName.Heidi2RequestAccessBanner,
      action: ButtonAction.Heidi2RequestAccessBannerLearnMore,
      label: intl.formatMessage(messages.learnMore),
    });
  }, [intl, ports]);

  const handleRequestAccess = useCallback(() => {
    ports.trackButtonClicked({
      screen_name: ScreenName.Heidi2RequestAccessBanner,
      action: ButtonAction.Heidi2RequestAccessBannerRequest,
      label: intl.formatMessage(messages.requestAccess),
    });
    setRequestAccessModalOpen(true);
  }, [intl, ports, setRequestAccessModalOpen]);

  const handleDismiss = useCallback(() => {
    ports.trackButtonClicked({
      screen_name: ScreenName.Heidi2RequestAccessBanner,
      action: ButtonAction.Heidi2RequestAccessBannerDismiss,
      label: intl.formatMessage(messages.dismissLabel),
    });
    setIsHiddenLocally(true);
    markModalAsViewed({
      modal_id: ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_REQUEST_ACCESS_BANNER,
      increment_count: false,
      action: ModalViewAction.dismiss,
    });
  }, [intl, markModalAsViewed, ports]);

  if (!isVisible) return null;

  return (
    <section
      className="relative flex h-9 w-full shrink-0 items-center justify-center gap-3 bg-entity-yellow-muted p-1 text-entity-yellow-foreground"
      data-testid="heidi-2-request-access-banner"
    >
      <div className="flex items-center gap-2">
        <HeidiLogoMark className="size-4" />
        <div className="flex items-baseline gap-1">
          <TypographyCaptionBold>
            <FormattedMessage {...messages.body} />
          </TypographyCaptionBold>
          <TypographyCaption>
            <Link
              href={HEIDI_2_GETTING_STARTED_URL}
              variant="inline"
              size="inherit"
              target="_blank"
              rel="noopener noreferrer"
              className="text-entity-yellow-foreground decoration-entity-yellow-foreground"
              onClick={handleLearnMore}
              data-testid="heidi-2-request-access-banner-learn-more"
            >
              <FormattedMessage {...messages.learnMore} />
            </Link>
          </TypographyCaption>
        </div>
      </div>
      <Button
        type="button"
        variant="surface"
        size="xs"
        onClick={handleRequestAccess}
        data-testid="heidi-2-request-access-banner-request"
      >
        <FormattedMessage {...messages.requestAccess} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        iconOnly
        aria-label={intl.formatMessage(messages.dismissLabel)}
        className="absolute end-1.5"
        onClick={handleDismiss}
        data-testid="heidi-2-request-access-banner-dismiss"
      >
        <X />
      </Button>
    </section>
  );
};
